import React, { useState } from "react";
import useAdminBlog from "../hooks/useAdminBlog";
import {
  Button,
  FormControl,
  Grid,
  Paper,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  ListItemIcon,
  CircularProgress,
  Chip,
  Box,
} from "@mui/material";
import useAPI from "../hooks/useAPI";
import { blogsActions } from "../state/blogs";
import { useDispatch } from "react-redux";
import UploadImage from "./UploadImage";
import RichTextEditor from "./RichTextEditor";
import { Storage } from "aws-amplify";
import shortid from "shortid";

export default function AdminBlog(props) {
  const {
    currentItem,
    onSave,
    callback,
    canEdit = true,
    onCancelAction,
  } = props;
  useAdminBlog(props);
  const dispatch = useDispatch();

  const [id, setId] = useState(currentItem?.id || 0);
  const [title, setTitle] = useState(currentItem?.title || "");
  const [description, setDescription] = useState(currentItem?.content || "");
  const [tags, setTags] = useState(currentItem?.tags || []);
  const [tagInput, setTagInput] = useState("");
  const [illustrations, setIllustrations] = useState(currentItem?.photos || []);
  const [associatedMerchants, setAssociatedMerchants] = useState(
    currentItem?.metadata?.associatedMerchants || []
  );
  const [removedMerchants, setRemovedMerchants] = useState([]);
  const [openMerchantsDialog, setOpenMerchantsDialog] = useState(false);
  console.log("illustrations", illustrations);
  // Mettre à jour les états quand currentItem change
  React.useEffect(() => {
    if (currentItem) {
      setId(currentItem.id || 0);
      setTitle(currentItem.title || "");
      setDescription(currentItem.content || "");
      setTags(currentItem.tags || []);
      setIllustrations(currentItem.photos || []);
      setAssociatedMerchants(currentItem.metadata?.associatedMerchants || []);
      setRemovedMerchants([]);
    } else {
      setId(0);
      setTitle("");
      setDescription("");
      setTags([]);
      setIllustrations([]);
      setAssociatedMerchants([]);
      setRemovedMerchants([]);
    }
  }, [currentItem]);

  const { data: blogs, query: fetchBlogs } = useAPI({
    type: "get",
    url: `articles`,
    defer: true,
  });

  const { query: postItem } = useAPI({
    type: "post",
    url: `article`,
    defer: true,
  });

  const { query: updateItem } = useAPI({
    type: "put",
    url: `article/${id}`,
    defer: true,
  });

  const {
    data: merchantsData,
    query: fetchMerchants,
    loading: merchantsLoading,
  } = useAPI({
    type: "get",
    url: `merchants`,
    defer: true,
  });

  React.useEffect(() => {
    if (blogs && blogs.data) {
      dispatch(blogsActions.fetchAll(blogs.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogs]);

  const canSave = title.length === 0;

  const resetForm = () => {
    setId(0);
    setTitle("");
    setDescription("");
    setTags([]);
    setIllustrations([]);
    setAssociatedMerchants([]);
    setRemovedMerchants([]);
  };

  const handleSaveBlog = async () => {
    try {
      const bodyData = {
        title,
        slug: title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)+/g, ""),
        content: description,
        tags,
        illustrations,
        metadata: {
          associatedMerchants,
        },
      };

      console.log("Saving blog with data:", { id, ...bodyData });

      if (id && id > 0) {
        await updateItem({
          url: `article/${id}`,
          body: {
            articleUpdated: bodyData,
            removed: removedMerchants,
          },
        });
      } else {
        await postItem({
          body: bodyData,
        });
      }

      await fetchBlogs();
      resetForm();
      onSave({
        success: true,
        message: "Article de blog sauvegardé avec succès",
      });
      callback?.();
    } catch (err) {
      console.error("Erreur lors de la sauvegarde de l'article de blog:", err);
      alert("Erreur lors de la sauvegarde de l'article de blog.");
    }
  };

  const handleIllustrationUpload = async (e) => {
    try {
      for (let i = 0; i < e.target.files.length; i++) {
        const file = e.target.files[i];
        const filename = `kadomi/blogs/${shortid.generate()}-${file.name}`;

        await Storage.put(filename, file, {
          contentType: file.type,
          progressCallback(progress) {
            const progressPercent = (progress.loaded / progress.total) * 100;
            console.log(`Uploaded: ${Math.round(progressPercent)}%`);
          },
        });

        setIllustrations((prev) => [...prev, filename]);
      }
    } catch (error) {
      console.error("Authentication or upload error:", error);
    }
  };

  const handleRemoveIllustration = (index) => {
    setIllustrations(illustrations.filter((_, i) => i !== index));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const handleDeleteTag = (tagToDelete) => {
    setTags(tags.filter((tag) => tag !== tagToDelete));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <React.Fragment>
      <Grid container spacing={3}>
        <Grid md={6} item xs={12}>
          <Paper elevation={3} style={{ padding: 20, margin: 20 }}>
            <h2>{id > 0 && `#${id} - `}Article de Blog</h2>
            <p>
              Veuillez remplir les informations ci-dessous pour créer ou
              modifier un article de blog.
            </p>

            <FormControl fullWidth style={{ marginBottom: 20 }}>
              <TextField
                id="blog-title"
                label="Titre *"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Entrez le titre de l'article"
              />
            </FormControl>

            <FormControl fullWidth style={{ marginBottom: 20 }}>
              <Typography variant="subtitle2" gutterBottom>
                Contenu de l'article
              </Typography>
              <RichTextEditor
                content={description}
                onChange={(html) => setDescription(html)}
                disabled={!canEdit}
                placeholder="Rédigez le contenu de votre article de blog ici..."
              />
            </FormControl>

            <hr style={{ margin: "20px 0" }} />

            <Typography variant="h6" gutterBottom>
              Tags
            </Typography>

            <Box display="flex" gap={1} marginBottom={2}>
              <TextField
                size="small"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ajouter un tag"
                fullWidth
              />
              <Button variant="contained" onClick={handleAddTag}>
                Ajouter
              </Button>
            </Box>

            <Box display="flex" flexWrap="wrap" gap={1} marginBottom={2}>
              {tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  onDelete={() => handleDeleteTag(tag)}
                  color="primary"
                  variant="outlined"
                />
              ))}
              {tags.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  Aucun tag ajouté
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid md={6} item xs={12}>
          <Paper elevation={3} style={{ padding: 20, margin: 20 }}>
            <h2>Illustrations</h2>
            <p>
              Ajoutez des illustrations pour votre article de blog. Vous pouvez
              ajouter plusieurs images.
            </p>

            <div style={{ marginBottom: 20 }}>
              <UploadImage onFileChange={handleIllustrationUpload} />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                gap: "10px",
                marginTop: 20,
              }}
            >
              {illustrations.map((illustration, index) => (
                <div
                  key={index}
                  style={{
                    position: "relative",
                    paddingBottom: "100%",
                    overflow: "hidden",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                  }}
                >
                  <img
                    src={`${process.env.REACT_APP_BUCKETS3}${illustration}`}
                    alt={`Illustration ${index + 1}`}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={() => handleRemoveIllustration(index)}
                    style={{
                      position: "absolute",
                      top: 5,
                      right: 5,
                      minWidth: 30,
                      padding: "4px 8px",
                    }}
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>

            {illustrations.length === 0 && (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ textAlign: "center", marginTop: 20 }}
              >
                Aucune illustration ajoutée pour le moment
              </Typography>
            )}
          </Paper>

          <Paper elevation={3} style={{ padding: 20, margin: 20 }}>
            <h2>Informations</h2>
            <p>
              Cette interface permet de gérer les articles de blog de votre
              plateforme. Un article peut avoir plusieurs tags pour faciliter la
              recherche et plusieurs illustrations.
            </p>
            <p>
              Assurez-vous de remplir au moins le titre de l'article. Les autres
              informations sont optionnelles mais recommandées.
            </p>
          </Paper>
        </Grid>
      </Grid>

      <div style={{ height: 50 }}>
        <Button variant="contained" disabled={canSave} onClick={handleSaveBlog}>
          {id ? "Modifier" : "Créer"}
        </Button>
        {id && id > 0 && (
          <Button
            variant="outlined"
            color="primary"
            style={{ marginLeft: 10 }}
            onClick={() => {
              setOpenMerchantsDialog(true);
              fetchMerchants();
            }}
          >
            Points de vente ({associatedMerchants.length})
          </Button>
        )}
        <Button
          variant="contained"
          style={{ marginLeft: 10 }}
          onClick={resetForm}
        >
          Réinitialiser
        </Button>
        <Button
          style={{ marginLeft: 10 }}
          variant="contained"
          onClick={onCancelAction}
        >
          Annuler
        </Button>
      </div>

      {/* Dialog d'association aux merchants */}
      <Dialog
        open={openMerchantsDialog}
        onClose={() => setOpenMerchantsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Association aux points de vente
          <Typography variant="body2" color="textSecondary">
            Sélectionnez les points de vente associés à cet article
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {merchantsLoading ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              <CircularProgress />
            </div>
          ) : (
            <List>
              {merchantsData?.merchants?.map((merchant) => {
                const isAssociated = associatedMerchants.some(
                  (assocMerchant) => assocMerchant.id === merchant.id
                );
                return (
                  <ListItem
                    key={merchant.id}
                    button
                    onClick={() => {
                      if (isAssociated) {
                        const removedMerchant = associatedMerchants.find(
                          (assocMerchant) => assocMerchant.id === merchant.id
                        );
                        if (removedMerchant) {
                          setRemovedMerchants([
                            ...removedMerchants,
                            removedMerchant,
                          ]);
                        }
                        setAssociatedMerchants(
                          associatedMerchants.filter(
                            (assocMerchant) => assocMerchant.id !== merchant.id
                          )
                        );
                      } else {
                        setRemovedMerchants(
                          removedMerchants.filter(
                            (rMerchant) => rMerchant.id !== merchant.id
                          )
                        );
                        setAssociatedMerchants([
                          ...associatedMerchants,
                          { id: merchant.id, name: merchant.name },
                        ]);
                      }
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={isAssociated}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${merchant.id}# ${merchant.name}`}
                      secondary={
                        <>
                          {merchant.location && `📍 ${merchant.location}`}
                          {merchant.phoneNumber && (
                            <span style={{ marginLeft: 10 }}>
                              📞 {merchant.phoneNumber}
                            </span>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                );
              })}
              {merchantsData?.merchants?.length === 0 && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ textAlign: "center", padding: 20 }}
                >
                  Aucun point de vente disponible
                </Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMerchantsDialog(false)}>Annuler</Button>
          <Button
            onClick={() => {
              setOpenMerchantsDialog(false);
            }}
            variant="contained"
            color="primary"
          >
            Valider ({associatedMerchants.length} sélectionné
            {associatedMerchants.length > 1 ? "s" : ""})
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
